import React, { useState } from 'react';
import { useCustomNavigate } from '../../hooks/useCustomNavigate';
import { CustomLink } from '../../components/CustomLink';
import { Button } from '../../components/Button';
import { Mail, ArrowLeft, AlertCircle, Send } from 'lucide-react';
import { authService } from '../../services/authService';

function ForgotPassword() {
  const navigate = useCustomNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setFormError(null);

    try {
      const response = await authService.forgotPassword(email);
      
      if (response.success) {
        setEmailSent(true);
      } else {
        setFormError(response.error || 'Произошла ошибка при отправке запроса');
      }
    } catch (error: any) {
      const errorMessage = error.message || 'Ошибка при отправке запроса';
      setFormError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-900 dark:to-gray-800 transition-colors duration-200">
      <div className="fixed top-4 right-4">
        <button
          onClick={() => document.documentElement.classList.toggle('dark')}
          className="p-2 rounded-full bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
          aria-label="Toggle theme"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-800 dark:text-gray-200">
            <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z"></path>
          </svg>
        </button>
      </div>
      <div className="max-w-md mx-auto px-4 py-12">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-8">
          <div className="mb-6">
            <button
              onClick={() => navigate('/auth/login')}
              className="flex items-center text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              <span className="text-sm">Назад ко входу</span>
            </button>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">
            Восстановление пароля
          </h2>

          {formError && (
            <div className="mb-6 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <div className="flex items-center">
                <AlertCircle className="w-5 h-5 text-red-500 dark:text-red-400 flex-shrink-0 mr-2" />
                <p className="text-sm text-red-600 dark:text-red-400">{formError}</p>
              </div>
            </div>
          )}

          {emailSent ? (
            <div className="text-center">
              <div className="mb-4 flex justify-center">
                <Send className="w-16 h-16 text-green-500 dark:text-green-400" />
              </div>
              <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-2">
                Проверьте вашу почту
              </h3>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                Мы отправили инструкции по восстановлению пароля на адрес <strong>{email}</strong>. 
                Пожалуйста, проверьте вашу почту и следуйте указанным инструкциям.
              </p>
              <button
                onClick={() => navigate('/auth/login')}
                className="w-full flex items-center justify-center px-6 py-3 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Вернуться ко входу
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-10 p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/25"
                    placeholder="Введите ваш email"
                    required
                  />
                </div>
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  Мы отправим ссылку для сброса пароля на ваш email
                </p>
              </div>

              <Button
                type="submit"
                disabled={isLoading}
                variant="primary"
                fullWidth
                className="flex items-center justify-center"
              >
                {isLoading ? 'Отправка...' : 'Отправить инструкции'}
              </Button>
            </form>
          )}

          <p className="mt-6 text-center text-sm text-gray-500 dark:text-gray-400">
            Вспомнили пароль?{' '}
            <CustomLink
              to="/auth/login"
              className="font-medium text-blue-600 dark:text-blue-400 hover:underline"
            >
              Вернуться ко входу
            </CustomLink>
          </p>
        </div>
      </div>
    </div>
  );
}

export default ForgotPassword; 